import {
  allowsInPerson,
  allowsOnline,
  type AttendanceMode,
} from "@/constants/attendanceMode";

type BookingOptionLike = Record<string, unknown> & {
  key?: string | number | null;
  bookingTypeId?: string | number | null;
  label?: string | number | null;
  allowRemote?: boolean;
  attendanceMode?: AttendanceMode;
  schedule?: Array<{ date?: string | null }> | null;
  scheduleVersion?: string | number | null;
};

type DoctorSummary = Record<string, unknown> & {
  tenantXId?: string | number | null;
  resourceId?: string | number | null;
};

type DoctorLike = Record<string, unknown> & {
  entryKey?: string | number | null;
  id?: string | number | null;
  tenantXId?: string | number | null;
  tenantId?: string | number | null;
  resourceId?: string | number | null;
  rawSummary?: DoctorSummary | null;
  bookingOptions?: BookingOptionLike[] | null;
  baseDateIso?: string | null;
};

export function normalizeBookingKey(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized.length ? normalized : null;
  }
  if (typeof value === "object") {
    const candidate = value as BookingOptionLike;
    const raw =
      candidate.key ?? candidate.bookingTypeId ?? candidate.label ?? null;
    if (raw === null || raw === undefined) {
      return null;
    }
    const normalized = String(raw).trim();
    return normalized.length ? normalized : null;
  }
  return null;
}

export function getDoctorKey(
  doctor: DoctorLike | null | undefined,
): string | null {
  if (!doctor || typeof doctor !== "object") {
    return null;
  }
  if (doctor.entryKey !== undefined && doctor.entryKey !== null) {
    return String(doctor.entryKey);
  }
  const rawSummary = doctor.rawSummary;
  if (
    rawSummary &&
    rawSummary.tenantXId !== undefined &&
    rawSummary.resourceId !== undefined
  ) {
    return `${rawSummary.tenantXId}:${rawSummary.resourceId}`;
  }
  if (doctor.tenantXId !== undefined && doctor.resourceId !== undefined) {
    return `${doctor.tenantXId}:${doctor.resourceId}`;
  }
  if (doctor.id !== undefined && doctor.id !== null) {
    return String(doctor.id);
  }
  return null;
}

export function getDefaultBookingKey(
  doctor: DoctorLike | null | undefined,
): string | null {
  if (!doctor || typeof doctor !== "object") {
    return null;
  }
  const options = Array.isArray(doctor.bookingOptions)
    ? doctor.bookingOptions
    : [];
  if (!options.length) {
    return null;
  }
  const fallback = options[0].key ?? null;
  return fallback !== null && fallback !== undefined ? String(fallback) : null;
}

export function resolveSelectedBookingKey(
  doctor: DoctorLike | null | undefined,
  selectedBookingKeys: Record<string, string> | null | undefined,
): string | null {
  const doctorKey = getDoctorKey(doctor);
  if (!doctorKey) {
    return null;
  }
  const storedKey = selectedBookingKeys ? selectedBookingKeys[doctorKey] : null;
  const options: BookingOptionLike[] =
    doctor && Array.isArray(doctor.bookingOptions) ? doctor.bookingOptions : [];
  if (storedKey !== null && storedKey !== undefined) {
    const exists = options.some((option: BookingOptionLike) => {
      if (!option) {
        return false;
      }
      const optionKey = option.key ?? option.bookingTypeId ?? null;
      if (optionKey === null || optionKey === undefined) {
        return false;
      }
      return String(optionKey) === String(storedKey);
    });
    if (exists) {
      return String(storedKey);
    }
  }
  return getDefaultBookingKey(doctor);
}

export function resolvePreferredBookingKey(
  doctor: DoctorLike | null | undefined,
  preferredOnline: boolean | null,
): string | null {
  if (!doctor || typeof doctor !== "object") {
    return null;
  }
  const options: BookingOptionLike[] = Array.isArray(doctor.bookingOptions)
    ? doctor.bookingOptions
    : [];
  if (!options.length) {
    return null;
  }

  const normalizedOptions = options
    .map((option) => {
      if (!option) {
        return null;
      }
      const optionKey = option.key ?? option.bookingTypeId ?? null;
      if (optionKey === null || optionKey === undefined) {
        return null;
      }
      return {
        option,
        key: String(optionKey),
      };
    })
    .filter(Boolean) as Array<{ option: BookingOptionLike; key: string }>;

  if (!normalizedOptions.length) {
    return null;
  }

  if (preferredOnline !== null) {
    const preferredEntry = normalizedOptions.find(
      (entry) =>
        preferredOnline
          ? allowsOnline(entry.option.attendanceMode)
          : allowsInPerson(entry.option.attendanceMode),
    );
    if (preferredEntry) {
      return preferredEntry.key;
    }
  }

  return normalizedOptions[0].key;
}

export function isScheduleCurrent(
  doctor: DoctorLike | null | undefined,
  option: BookingOptionLike | null | undefined,
): boolean {
  if (!option) {
    return false;
  }
  const schedule = Array.isArray(option.schedule) ? option.schedule : [];
  if (!schedule.length) {
    return false;
  }
  const baseDateIso =
    doctor && typeof doctor.baseDateIso === "string"
      ? doctor.baseDateIso
      : null;
  const scheduleStart =
    schedule[0] && typeof schedule[0].date === "string"
      ? schedule[0].date
      : null;
  if (!baseDateIso || !scheduleStart) {
    return true;
  }
  return String(baseDateIso).indexOf(scheduleStart) === 0;
}

export function hasCalendarSchedule(
  option: BookingOptionLike | null | undefined,
): boolean {
  if (!option) {
    return false;
  }
  const version = Number(option.scheduleVersion);
  return Number.isFinite(version) && version > 0;
}
